<script lang="ts" setup>
  import { marked } from 'marked'
  import { cva } from 'class-variance-authority'
  import { FARE_TABLE_CLUB_LEVEL } from '@/components/fare-table/types'
  import { fareHeaderColorVariants } from '@/components/fare-table/fareHeaderVariants'
  import FareTableServiceSummary from '@/storyblok/content/fare-table/ServiceSummary.vue'
  import FareTableServiceBody from '@/storyblok/content/fare-table/ServiceBody.vue'
  import type { TFareTableClubLevel } from '@/components/fare-table/types'
  import type { CSSProperties } from 'vue'
  import type { AssetStoryblok, ServiceSummaryStoryblok } from '@/storyblok/component-types-sb'

  type RowIndex = string | number

  const mobileRoot = ref<HTMLElement | null>(null)
  const mobileHeaderScroller = ref<HTMLElement | null>(null)
  const minFareCellWidthPx = 154
  const fareCellWidthPx = ref(minFareCellWidthPx)
  const isSyncingScroll = ref(false)

  const props = withDefaults(
    defineProps<{
      services: any[]
      visibleFares: any[]
      clubLogo?: AssetStoryblok
      clubLevel?: TFareTableClubLevel
      findFareService: (fare: any, service: any) => any
      hasExpandedContent: (service: ServiceSummaryStoryblok) => boolean
      isRowExpanded: (service: ServiceSummaryStoryblok, index: RowIndex) => boolean
      toggleRowExpanded: (service: ServiceSummaryStoryblok, index: RowIndex) => void
    }>(),
    {
      services: () => [],
      visibleFares: () => [],
      clubLogo: undefined,
      clubLevel: FARE_TABLE_CLUB_LEVEL.none
    }
  )

  const headerClass = cva('border-r border-border px-3 py-2 text-subtitle-2 last:border-r-0', {
    variants: {
      key: {
        ...fareHeaderColorVariants,
        default: ''
      }
    },
    defaultVariants: {
      key: 'default'
    }
  })

  const fareColumnsStyle = computed<CSSProperties>(() => ({
    '--fare-columns': Math.max(props.visibleFares.length, 1),
    '--fare-cell-width': `${fareCellWidthPx.value}px`,
    minWidth: `calc(${Math.max(props.visibleFares.length, 1)} * ${fareCellWidthPx.value}px)`
  }))

  const updateFareCellWidth = () => {
    const headerWidth = mobileHeaderScroller.value?.clientWidth ?? 0
    const fareCount = Math.max(props.visibleFares.length, 1)
    if (!headerWidth) {
      fareCellWidthPx.value = minFareCellWidthPx
      return
    }

    fareCellWidthPx.value = Math.max(minFareCellWidthPx, Math.floor(headerWidth / fareCount))
  }

  const isServiceFull = (service: any) => service?.full === true || service?.full === 'true'
  const resolveFareService = (fare: any, service: any) => props.findFareService(fare, service)

  const getRowScrollers = () =>
    Array.from(mobileRoot.value?.querySelectorAll<HTMLElement>('[data-fare-row-scroller]') ?? [])

  const syncRowsToHeader = () => {
    const nextScrollLeft = mobileHeaderScroller.value?.scrollLeft ?? 0
    getRowScrollers().forEach((rowEl) => {
      rowEl.scrollLeft = nextScrollLeft
    })
  }

  const onHeaderScroll = () => {
    if (isSyncingScroll.value) return
    const headerEl = mobileHeaderScroller.value
    if (!headerEl) return

    isSyncingScroll.value = true
    const nextScrollLeft = headerEl.scrollLeft
    getRowScrollers().forEach((rowEl) => {
      if (rowEl.scrollLeft === nextScrollLeft) return
      rowEl.scrollLeft = nextScrollLeft
    })
    requestAnimationFrame(() => {
      isSyncingScroll.value = false
    })
  }

  const onRowScroll = (event: Event) => {
    if (isSyncingScroll.value) return
    const sourceRow = event.target instanceof HTMLElement ? event.target : null
    const headerEl = mobileHeaderScroller.value
    if (!sourceRow || !headerEl) return

    isSyncingScroll.value = true
    const nextScrollLeft = sourceRow.scrollLeft
    if (headerEl.scrollLeft !== nextScrollLeft) {
      headerEl.scrollLeft = nextScrollLeft
    }

    getRowScrollers().forEach((rowEl) => {
      if (rowEl === sourceRow) return
      if (rowEl.scrollLeft === nextScrollLeft) return
      rowEl.scrollLeft = nextScrollLeft
    })
    requestAnimationFrame(() => {
      isSyncingScroll.value = false
    })
  }

  onMounted(() => {
    updateFareCellWidth()
    nextTick(() => {
      syncRowsToHeader()
    })
    window.addEventListener('resize', updateFareCellWidth)
  })

  onBeforeUnmount(() => {
    window.removeEventListener('resize', updateFareCellWidth)
  })

  watch(
    [() => props.visibleFares.length, () => props.services.length],
    () => {
      nextTick(() => {
        updateFareCellWidth()
        syncRowsToHeader()
      })
    },
    { flush: 'post' }
  )
</script>

<template>
  <div
    ref="mobileRoot"
    class="md:hidden"
  >
    <div class="sticky top-14 z-10 bg-grey pb-1">
      <div
        ref="mobileHeaderScroller"
        class="overflow-x-auto"
        @scroll="onHeaderScroll"
      >
        <div
          class="grid w-max overflow-hidden rounded-t-sm border border-b-0 border-border bg-gradient-to-b from-white to-grey [grid-template-columns:repeat(var(--fare-columns),var(--fare-cell-width))]"
          :style="fareColumnsStyle"
        >
          <div
            v-for="fare in visibleFares"
            :key="fare.key || fare._uid"
            :class="headerClass({ key: fare.key })"
          >
            <span class="inline-block max-w-full break-words">
              {{ fare.title }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <div class="grid grid-cols-1 gap-0">
      <div
        v-for="(service, index) in services"
        :key="service._uid || index"
        class="rounded-sm border border-border bg-gradient-to-b from-white to-grey"
      >
        <div class="border-b border-border p-3">
          <FareTableServiceSummary
            :blok="service"
            :logo="clubLogo"
            :row-expanded="isRowExpanded(service, index)"
            :expandable="!isServiceFull(service) && hasExpandedContent(service)"
            @update:expanded="toggleRowExpanded(service, index)"
          />
        </div>
        <template v-if="!isServiceFull(service)">
          <div
            data-fare-row-scroller
            class="overflow-x-auto overscroll-x-contain rounded-b-sm"
            @scroll="onRowScroll"
          >
            <div
              v-if="visibleFares.length"
              class="grid w-max min-w-full rounded-b-sm [grid-template-columns:repeat(var(--fare-columns),var(--fare-cell-width))]"
              :style="fareColumnsStyle"
            >
              <div
                v-for="fare in visibleFares"
                :key="`service-mobile-${service._uid || index}-${fare.key || fare._uid}`"
                class="border-r border-border p-3 last:border-r-0"
              >
                <FareTableServiceBody
                  v-if="resolveFareService(fare, service)"
                  :blok="resolveFareService(fare, service)"
                  :logo="clubLogo"
                  :club-level="clubLevel"
                  :expanded="isRowExpanded(service, index)"
                />
              </div>
            </div>
          </div>
        </template>
        <div
          v-else
          class="p-3 text-subtitle-3"
        >
          <div v-html="marked(service.description)" />
        </div>
      </div>
    </div>
  </div>
</template>
